'use client';

import { useState, useCallback, useTransition, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import type {
  FetchParams,
  PageResult,
  UseInfiniteLoaderOptions,
  UseInfiniteLoaderReturn,
} from './types';

const DEFAULT_LIMIT = 24;

export function useInfiniteLoader<TItem>(
  options: UseInfiniteLoaderOptions<TItem>,
): UseInfiniteLoaderReturn<TItem> {
  const {
    fetchAction,
    initialData,
    initialCursor,
    limit = DEFAULT_LIMIT,
    paginationType = 'cursor',
  } = options;

  const [items, setItems] = useState<TItem[]>(initialData);
  const [hasMore, setHasMore] = useState<boolean>(
    paginationType === 'cursor'
      ? initialCursor !== null
      : initialData.length >= limit,
  );
  const [error, setError] = useState<Error | null>(null);
  const [pagesLoaded, setPagesLoaded] = useState(1);
  const [isPending, startTransition] = useTransition();

  // Transient values stored in refs so loadMore stays stable
  // and doesn't recreate on every page load (rerender-use-ref-transient-values)
  const cursorRef = useRef<string | null>(initialCursor);
  const offsetRef = useRef<number>(initialData.length);
  const fetchActionRef = useRef(fetchAction);
  fetchActionRef.current = fetchAction;
  const isLoadingRef = useRef(false);
  const generationRef = useRef(0);

  const { ref: sentinelRef, inView } = useInView({
    rootMargin: '200px',
    threshold: 0,
  });

  const loadMore = useCallback(() => {
    if (isLoadingRef.current || !hasMore) return;
    isLoadingRef.current = true;
    setError(null);

    const params: FetchParams =
      paginationType === 'cursor'
        ? { kind: 'cursor', cursor: cursorRef.current, limit }
        : { kind: 'offset', offset: offsetRef.current, limit };

    const generation = generationRef.current;

    startTransition(async () => {
      try {
        const result: PageResult<TItem> = await fetchActionRef.current(params);

        // Discard results if reset() was called during the fetch
        if (generation !== generationRef.current) return;

        setItems((prev) => [...prev, ...result.items]);
        setPagesLoaded((prev) => prev + 1);

        if (paginationType === 'cursor') {
          cursorRef.current = result.nextCursor;
          if (result.nextCursor === null) setHasMore(false);
        } else {
          offsetRef.current += result.items.length;
          if (result.items.length < limit) setHasMore(false);
        }
      } catch (err) {
        if (generation !== generationRef.current) return;
        setError(
          err instanceof Error
            ? err
            : new Error('Failed to load more items', { cause: err }),
        );
      } finally {
        isLoadingRef.current = false;
      }
    });
  }, [hasMore, limit, paginationType]);

  const reset = useCallback(() => {
    generationRef.current += 1;
    setItems(initialData);
    cursorRef.current = initialCursor;
    offsetRef.current = initialData.length;
    setHasMore(
      paginationType === 'cursor'
        ? initialCursor !== null
        : initialData.length >= limit,
    );
    setError(null);
    setPagesLoaded(1);
    isLoadingRef.current = false;
  }, [initialData, initialCursor, paginationType, limit]);

  // Auto-trigger loadMore when sentinel is in view
  useEffect(() => {
    if (inView && hasMore && !isLoadingRef.current) {
      loadMore();
    }
  }, [inView, hasMore, loadMore]);

  return {
    items,
    isLoading: isPending,
    error,
    hasMore,
    loadMore,
    sentinelRef,
    reset,
    pagesLoaded,
  };
}
